import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Home, Dumbbell, Users, CalendarCheck, Award, ShoppingBag, MessageCircle, Zap,
  LayoutDashboard, CalendarClock, Wallet, UserCog, ShieldCheck, Package, Repeat,
  Menu, Globe, LogOut, Star,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Logo, Avatar } from "@/components/brand";
import { useAuth } from "@/lib/auth";
import { usePrefs } from "@/lib/prefs";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const NAV = {
  member: [
    { to: "/discover", label: "Discover", icon: Home, end: true },
    { to: "/classes", label: "Classes", icon: Dumbbell },
    { to: "/trainers", label: "Trainers", icon: Users },
    { to: "/bookings", label: "Bookings", icon: CalendarCheck },
    { to: "/challenges", label: "Challenges", icon: Award },
    { to: "/plans", label: "Fit Pass", icon: Zap },
    { to: "/shop", label: "Shop", icon: ShoppingBag },
    { to: "/messages", label: "Messages", icon: MessageCircle, badge: "unread" },
  ],
  trainer: [
    { to: "/trainer", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/trainer/schedule", label: "Schedule", icon: CalendarClock },
    { to: "/trainer/earnings", label: "Earnings", icon: Wallet },
    { to: "/trainer/challenges", label: "Challenges", icon: Award },
    { to: "/messages", label: "Messages", icon: MessageCircle, badge: "unread" },
    { to: "/trainer/profile", label: "Profile", icon: UserCog },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/trainers", label: "Trainers", icon: ShieldCheck },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck },
    { to: "/admin/challenges", label: "Challenges", icon: Award },
    { to: "/admin/orders", label: "Orders", icon: Package },
    { to: "/admin/rates", label: "FX Rates", icon: Repeat },
  ],
};

function NavItems({ items, unread, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink key={it.to} to={it.to} end={it.end} onClick={onNavigate}
            data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) => cn(
              "flex items-center gap-3 h-11 px-3 rounded-md font-body font-semibold text-sm transition-colors",
              isActive ? "bg-brand text-white" : "text-ink hover:bg-brand-soft",
            )}>
            <Icon size={18} />
            <span className="flex-1">{it.label}</span>
            {it.badge === "unread" && unread > 0 ? (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center">{unread}</span>
            ) : null}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { language, currency } = usePrefs();
  const [open, setOpen] = useState(false);
  const items = NAV[user?.role] ?? NAV.member;
  const unread = useQuery({
    queryKey: ["conversations", "unread"],
    queryFn: () => api("/conversations/unread-count"),
    enabled: !!user && user.role !== "admin",
    refetchInterval: 20000,
  });
  const unreadCount = unread.data?.unread ?? 0;

  const doLogout = async () => { await logout(); window.location.assign("/"); };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <Link to={user?.role === "admin" ? "/admin" : user?.role === "trainer" ? "/trainer" : "/discover"} className="px-3 py-5" onClick={() => setOpen(false)}>
        <Logo size={28} />
      </Link>
      <div className="flex-1 overflow-y-auto px-2">
        <NavItems items={items} unread={unreadCount} onNavigate={() => setOpen(false)} />
      </div>
      <div className="p-2 border-t border-divider space-y-1">
        <Link to="/preferences" onClick={() => setOpen(false)} data-testid="nav-preferences" className="flex items-center gap-3 h-11 px-3 rounded-md font-body font-semibold text-sm text-ink hover:bg-brand-soft">
          <Globe size={18} /><span className="flex-1">{language.toUpperCase()} · {currency}</span>
        </Link>
        <button onClick={doLogout} data-testid="nav-logout" className="w-full flex items-center gap-3 h-11 px-3 rounded-md font-body font-semibold text-sm text-ink hover:bg-brand-soft">
          <LogOut size={18} /><span>Log Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 border-r border-divider bg-white z-30">{SidebarInner}</aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 h-16 px-4 bg-white border-b border-divider">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-button" className="p-2 -ml-2 text-ink"><Menu size={24} /></button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">{SidebarInner}</SheetContent>
        </Sheet>
        <Logo size={26} />
        <div className="flex-1" />
        <Link to={user?.role === "trainer" ? "/trainer/profile" : "/profile"} data-testid="topbar-avatar">
          <Avatar uri={user?.avatar} size={36} name={user?.name} />
        </Link>
      </header>

      <main className="lg:pl-64 min-h-screen">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 pb-24">{children}</div>
      </main>
    </div>
  );
}

export { Star };
